import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { useSelector } from 'react-redux';
import { ROUTES } from '../constants/routes';
import apiForgotPassword from '../api/forgotPassword';
import {
  ButtonMain,
  ButtonInverted,
  TextInput,
  SuccessCheckmark,
} from '../atoms';

import {
  addErrorsForEmptyFields,
  isValidationSucceed,
} from '../utils/validation';
import { errorsSelector } from '../redux/ducks/requestApiError';

const initialValue: FormData = {
  email: '',
};
const initialErrorMessage: FormErrors = {
  email: null,
};
const ForgotPasswordForm = () => {
  const history = useHistory();
  const [value, setValue] = React.useState(initialValue);
  const [error, setError] = React.useState(
    initialErrorMessage,
  );
  const [
    instructionIsSent,
    setInstructionIsSent,
  ] = React.useState(false);

  const apiErrors = useSelector(
    errorsSelector,
  );

  const handleReminderSend = () => {
    apiForgotPassword
      .resetPasswordSendMail(value)
      .then(() => {
        setInstructionIsSent(true);
      })
      .catch(e => {
        const errorResponse =
          e?.response?.data?.email?.[0] ||
          e?.response?.data?.detail ||
          '';
        const lowerError = errorResponse.toLowerCase();
        if (lowerError.includes('company') || lowerError.includes('invalid') || lowerError.includes('not found') || lowerError.includes('does not exist')) {
          setError({
            email: 'This email address is associated with your company rather than an individual user account. To access the Client Portal, please contact our office to have your personal login credentials created.',
          });
        } else {
          setError({ email: errorResponse || 'Enter a valid email address' });
        }
      });
  };

  const handleChangeValue = e => {
    setValue({ email: e.target.value });
    setError({ email: "" });
  };

  const validateForm = () => {
    const newError = addErrorsForEmptyFields(
      value,
      error,
      'This field can not be empty',
    );

    setError(newError);

    return isValidationSucceed(newError);
  };

  const handleSubmit = e => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      handleReminderSend();
    }
  };

  const ErrorMessage = () => {
    return (
      <p style={{ display: 'flex', alignItems: 'center', padding: 0, margin: 0 }}>
        <WarningRoundedIcon /> {apiErrors.detail}
      </p>
    );
  };

  return (
    <>
      {!instructionIsSent && (
        <>
          <Description>
            We will send you an email with instructions on
            how to reset your password
          </Description>

          <Form onSubmit={handleSubmit}>
            <StyledTextInput
              label="Your Email"
              value={value.email}
              onChange={handleChangeValue}
              error={!!error.email || !!apiErrors.detail}
              helperText={error.email || (apiErrors.detail && <ErrorMessage />) }
            />
            <ButtonsWrapper>
              <StyledButtonMain type="submit">
                Send
              </StyledButtonMain>
              <StyledButtonInverted
                onClick={() => history.push(ROUTES.LOG_IN)}
              >
                Cancel
              </StyledButtonInverted>
            </ButtonsWrapper>
          </Form>
        </>
      )}

      {instructionIsSent && (
        <SuccessSendWrapper>
          <SuccessContent>
            <p>
              If an account exists for
              <span> {value.email}</span>, you will receive
              an email with instructions on how to reset your
              password.
            </p>
            <BackToLogin onClick={() => history.push(ROUTES.LOG_IN)}>
              Back to Login
            </BackToLogin>
          </SuccessContent>
        </SuccessSendWrapper>
      )}
    </>
  );
};

const Form = styled.form`
  width: 100%;
  margin-bottom: 73px;
`;

const StyledTextInput = styled(TextInput)`
  margin-top: 46px;
  width: 100%;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-top: 50px;
  justify-content: center;

  & button {
    height: 36px;
  }
`;

const StyledButtonMain = styled(ButtonMain)`
  margin-right: 16px;
  width: 112px;
`;

const StyledButtonInverted = styled(ButtonInverted)`
  width: 112px;
`;

const SuccessSendWrapper = styled.div.attrs({
  className: 'font-body2',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: var(--mainText);
  line-height: 20px;
  margin-top: 32px;
  margin-bottom: 344px;
  gap: 12px;

  & span {
    font-weight: bold;
  }
`;

const StyledSuccessCheckmark = styled(SuccessCheckmark)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  text-align: center;
`;

const ClientPortalLink = styled.span`
  color: var(--primary, #1d6fff);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: var(--primary-700, #1656cc);
  }
`;

const BackToLogin = styled.span`
  display: block;
  margin-top: 16px;
  text-align: center;
  color: var(--primary, #1d6fff);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: var(--primary-700, #1656cc);
  }
`;

const Description = styled.p.attrs({
  className: 'font-body2',
})`
  width: 100%;
  color: var(--secondaryText);
  margin-top: 0;
  text-align: center;
`;

interface Props {
  onReminderSend: () => void;
  value: FormData;
  setValue: (arg: FormData) => void;
  error: FormErrors;
  setError: (arg: FormErrors) => void;
  instructionIsSent: boolean;
}

interface FormErrors {
  [s: string]: string;
  email: string;
}

interface FormData {
  email: string;
}

export default ForgotPasswordForm;
